import { useState, useEffect, useRef, useCallback } from 'react';
import { salvarPlano } from '../api';

function camposDoPlano(plano) {
  return {
    caixasPorCamada:    plano?.caixas_por_camada ?? 1,
    numCamadas:         plano?.num_camadas ?? 1,
    orientacao:         plano?.layout_orientacao ?? 'longitudinal',
    sobreposicaoMm:     plano?.sobreposicao_mm ?? 50,
    caixasAlinhadas:    plano?.caixas_alinhadas ?? null,
    caixasGiradas:      plano?.caixas_giradas ?? 0,
    transbordoGiradaMm: plano?.transbordo_girada_mm ?? 0,
  };
}

export function usePlanoEditor(volumeId, planoInicial, modalidade = 'embalagem') {
  const [campos, setCampos] = useState(() => camposDoPlano(planoInicial));
  const [plano, setPlano]           = useState(planoInicial ?? null);
  const [salvando, setSalvando]     = useState(false);
  const [ultimoSalvo, setUltimoSalvo] = useState(null);
  const [erro, setErro]             = useState(null);
  const [camposSalvos, setCamposSalvos] = useState(() =>
    planoInicial?.id ? camposDoPlano(planoInicial) : null
  );

  const primeiroRender = useRef(!!planoInicial?.id);
  const debounceRef    = useRef(null);
  const filaSalvarRef   = useRef(Promise.resolve());
  const salvosPendentesRef = useRef(0);

  const executarSalvar = useCallback((snapshot) => {
    if (!volumeId) return Promise.resolve();
    salvosPendentesRef.current += 1;
    setSalvando(true);
    const tarefa = filaSalvarRef.current.then(async () => {
      setErro(null);
      try {
        const resultado = await salvarPlano(volumeId, {
          caixas_por_camada:    snapshot.caixasPorCamada,
          num_camadas:          snapshot.numCamadas,
          layout_orientacao:    snapshot.orientacao,
          sobreposicao_mm:      snapshot.sobreposicaoMm,
          caixas_alinhadas:     snapshot.caixasAlinhadas,
          caixas_giradas:       snapshot.caixasGiradas,
          transbordo_girada_mm: snapshot.transbordoGiradaMm,
        }, modalidade);
        setPlano(resultado);
        setCamposSalvos(snapshot);
        setUltimoSalvo(new Date());
      } catch (err) {
        setErro(err.message);
      } finally {
        salvosPendentesRef.current -= 1;
        if (salvosPendentesRef.current === 0) setSalvando(false);
      }
    });
    filaSalvarRef.current = tarefa;
    return tarefa;
  }, [volumeId, modalidade]);

  useEffect(() => {
    if (primeiroRender.current) {
      primeiroRender.current = false;
      return;
    }
    clearTimeout(debounceRef.current);
    const snapshot = { ...campos };
    debounceRef.current = setTimeout(() => executarSalvar(snapshot), 300);
    return () => clearTimeout(debounceRef.current);
  }, [campos, executarSalvar]);

  const setField = useCallback((field, value) => {
    setCampos(prev => {
      if (field === 'caixasPorCamada') {
        const caixasGiradas = prev.orientacao === 'longitudinal'
          ? Math.min(prev.caixasGiradas ?? 0, value)
          : 0;
        return {
          ...prev,
          caixasPorCamada: value,
          caixasAlinhadas: Math.max(0, value - caixasGiradas),
          caixasGiradas,
          transbordoGiradaMm: caixasGiradas > 0 ? prev.transbordoGiradaMm : 0,
        };
      }

      if (field === 'orientacao' && value === 'atravessada') {
        return {
          ...prev,
          orientacao: value,
          caixasAlinhadas: prev.caixasPorCamada,
          caixasGiradas: 0,
          transbordoGiradaMm: 0,
        };
      }

      return { ...prev, [field]: value };
    });
  }, []);

  const salvar = useCallback(() => {
    clearTimeout(debounceRef.current);
    executarSalvar({ ...campos });
  }, [campos, executarSalvar]);

  // Recebe um plano já calculado (do botão "Restaurar") e sincroniza o estado.
  const restaurar = useCallback((novoPlano) => {
    if (!novoPlano) return;
    primeiroRender.current = false;
    setCampos(camposDoPlano(novoPlano));
  }, []);

  const pendente = !camposSalvos || Object.keys(campos).some(
    campo => campos[campo] !== camposSalvos[campo]
  );

  return { campos, setField, plano, salvando, pendente, ultimoSalvo, erro, restaurar, salvar };
}
